import { useState } from 'react';
import { attachmentUrl } from '../lib/api.js';
import { formatAbsolute } from '../lib/format.js';
import { hardenedEmailSrcDoc } from '../lib/sanitize-email.js';
import type {
  AttachmentDto,
  EmailAttendee,
  EmailHeaders,
  MessageDto,
  ReadReceiptDto,
} from '../types.js';

interface Props {
  message: MessageDto;
  /** batch-202 — show the sender's name above inbound bubbles (group chats only). */
  showSender?: boolean;
}

/**
 * Per-message rendering.
 *
 * Chat channels (linkedin, telegram, whatsapp) keep the original
 * compact left/right-bubble timeline. Email gets a channel-native
 * "envelope + body" layout: subject as a header, addressing block
 * (from / to / cc / bcc / reply-to), attachment count, and the
 * message body with an optional "View formatted email" toggle that
 * mounts the HTML inside a fully sandboxed iframe.
 *
 * The sandboxed iframe (`sandbox=""` with no script flag) is the
 * isolation boundary: scripts cannot run, top-level navigation is
 * blocked, no cookies are forwarded. We deliberately do NOT
 * `dangerouslySetInnerHTML` into the surrounding document.
 */
export function MessageRow({ message, showSender }: Props) {
  if (message.channel === 'email') {
    return <EmailMessage message={message} />;
  }
  return <ChatBubble message={message} showSender={showSender} />;
}

function ChatBubble({ message, showSender }: { message: MessageDto; showSender?: boolean }) {
  const isOutbound = message.direction === 'outbound';
  // Inbox-rebuild Phase 2 — calm aligned bubbles: inbound = white surface card on
  // the grey thread; outbound = accent. The side + colour already convey direction,
  // so the old visible "Sent/Received" label is now sr-only (kept for a11y + tests).
  const side = isOutbound ? 'out' : 'in';
  const label = isOutbound ? 'Sent' : 'Received';

  return (
    <div className={`msg-wrap ${side}`}>
      {!isOutbound && showSender && message.sender_name ? (
        <div className="msg-sender" data-testid="message-sender-name">
          {message.sender_name}
        </div>
      ) : null}
      <div className={`msg-bubble ${side}`}>
        {message.subject ? <div className="msg-subject">{message.subject}</div> : null}
        <ChatBody message={message} />
        <MessageAttachments messageId={message.id} attachments={message.attachments ?? []} />
      </div>
      <div className="msg-foot">
        <span className="sr-only" data-testid="message-direction">
          {label}
        </span>
        <time dateTime={message.source_timestamp}>{formatAbsolute(message.source_timestamp)}</time>
        {message.read_receipt ? (
          <>
            <span className="msg-foot-sep">·</span>
            <ReadReceiptLabel receipt={message.read_receipt} />
          </>
        ) : null}
      </div>
    </div>
  );
}

/**
 * batch-204 — read receipt for an OUTBOUND message. "Read" for an email open (with
 * the time on hover); "Seen" / "Seen by N" for chat. The accent colour mirrors a
 * messenger's blue double-tick.
 */
function ReadReceiptLabel({ receipt }: { receipt: ReadReceiptDto }) {
  if (receipt.read_at) {
    return (
      <span className="msg-receipt" data-testid="read-receipt" title={`Read ${receipt.read_at}`}>
        ✓✓ Read
      </span>
    );
  }
  const label =
    receipt.seen_by_count && receipt.seen_by_count > 1
      ? `Seen by ${receipt.seen_by_count}`
      : 'Seen';
  return (
    <span className="msg-receipt" data-testid="read-receipt">
      ✓✓ {label}
    </span>
  );
}

function ChatBody({ message }: { message: MessageDto }) {
  if (message.body_text && message.body_text.trim().length > 0) {
    return <div className="whitespace-pre-wrap break-words text-sm">{message.body_text}</div>;
  }
  if (message.has_body_html || message.body_html) {
    return (
      <div className="text-sm italic text-zinc-400">HTML-only message — rich view not enabled.</div>
    );
  }
  // batch-203 — renderable attachments display themselves below (image / video /
  // audio / file chip), so don't also show a text label or the "No text content"
  // placeholder for a media-only message.
  if (message.attachments && message.attachments.length > 0) {
    return null;
  }
  // Text-less message we can describe (shared post / photo / file / voice note).
  if (message.attachment_summary) {
    return (
      <div className="text-sm italic text-zinc-400" data-testid="message-attachment-summary">
        {message.attachment_summary}
      </div>
    );
  }
  return (
    <div className="text-sm italic text-zinc-500">
      No text content (attachment, voice note, or system message).
    </div>
  );
}

/* ---------- batch-203: inline attachment media (same-origin proxy) ---------- */

function formatBytes(n: number | null): string | null {
  if (!n || n <= 0) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function MessageAttachments({
  messageId,
  attachments,
}: {
  messageId: string;
  attachments: AttachmentDto[];
}) {
  if (attachments.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-col gap-1.5" data-testid="message-attachments">
      {attachments.map((a) => (
        <AttachmentItem key={a.id} url={attachmentUrl(messageId, a.id)} att={a} />
      ))}
    </div>
  );
}

function AttachmentItem({ url, att }: { url: string; att: AttachmentDto }) {
  if (att.kind === 'image' || att.kind === 'sticker') {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block">
        <img
          src={url}
          alt={att.name ?? 'Image attachment'}
          loading="lazy"
          className="max-h-72 max-w-full rounded-lg object-contain"
          data-testid="attachment-image"
        />
      </a>
    );
  }
  if (att.kind === 'video') {
    return (
      <video
        src={url}
        controls
        className="max-h-72 max-w-full rounded-lg"
        data-testid="attachment-video"
      >
        {/* user-sent media has no caption track; empty track satisfies a11y. */}
        <track kind="captions" />
      </video>
    );
  }
  if (att.kind === 'audio') {
    return (
      <audio src={url} controls className="w-full" data-testid="attachment-audio">
        <track kind="captions" />
      </audio>
    );
  }
  const size = formatBytes(att.size);
  return (
    <a
      href={url}
      download={att.name ?? undefined}
      target="_blank"
      rel="noreferrer"
      className="inline-flex max-w-fit items-center gap-2 rounded-md border border-zinc-700 bg-zinc-800/60 px-2.5 py-1.5 text-xs text-zinc-200 hover:bg-zinc-800"
      data-testid="attachment-file"
    >
      <span aria-hidden>📎</span>
      <span className="max-w-[14rem] truncate">{att.name ?? 'Download file'}</span>
      {size ? <span className="text-zinc-400">· {size}</span> : null}
    </a>
  );
}

/* ---------- email-native renderer ---------- */

function EmailMessage({ message }: { message: MessageDto }) {
  const headers = message.email_headers ?? null;
  const attachmentCount = message.attachment_count ?? 0;
  const isOutbound = message.direction === 'outbound';
  const directionLabel = isOutbound ? 'Sent' : 'Received';

  return (
    <article
      data-testid="email-message"
      data-direction={message.direction}
      className="rounded-lg border border-zinc-800 bg-zinc-900/40"
    >
      <header className="space-y-1.5 border-b border-zinc-800 px-3 py-2">
        <div className="flex items-center gap-2 text-[10px] text-zinc-500">
          <span data-testid="message-direction">{directionLabel}</span>
          <span>·</span>
          <time dateTime={message.source_timestamp}>
            {formatAbsolute(message.source_timestamp)}
          </time>
          {message.read_receipt ? (
            <>
              <span>·</span>
              <ReadReceiptLabel receipt={message.read_receipt} />
            </>
          ) : null}
          {attachmentCount > 0 ? (
            <span
              data-testid="attachment-pill"
              className="ml-auto inline-flex items-center gap-1 rounded bg-zinc-900 px-1.5 py-0.5 text-[10px] text-zinc-300 ring-1 ring-inset ring-zinc-800"
              title={`${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}`}
            >
              <PaperclipIcon /> {attachmentCount}
            </span>
          ) : null}
        </div>
        <div className="text-sm font-semibold text-zinc-100">
          {message.subject ?? <span className="italic text-zinc-500">(no subject)</span>}
        </div>
        {headers ? <EmailHeadersBlock headers={headers} /> : null}
      </header>

      <div className="px-3 py-2">
        <EmailBody message={message} />
        <MessageAttachments messageId={message.id} attachments={message.attachments ?? []} />
      </div>
    </article>
  );
}

function EmailHeadersBlock({ headers }: { headers: EmailHeaders }) {
  return (
    <dl
      data-testid="email-headers"
      className="grid grid-cols-[3rem_1fr] gap-x-2 gap-y-0.5 text-[11px]"
    >
      {headers.from ? <HeaderLine label="From" attendees={[headers.from]} /> : null}
      {headers.to.length > 0 ? <HeaderLine label="To" attendees={headers.to} /> : null}
      {headers.cc.length > 0 ? <HeaderLine label="Cc" attendees={headers.cc} /> : null}
      {headers.bcc.length > 0 ? <HeaderLine label="Bcc" attendees={headers.bcc} /> : null}
      {headers.reply_to.length > 0 ? (
        <HeaderLine label="Reply-To" attendees={headers.reply_to} />
      ) : null}
    </dl>
  );
}

function HeaderLine({ label, attendees }: { label: string; attendees: EmailAttendee[] }) {
  return (
    <>
      <dt className="text-zinc-500">{label}</dt>
      <dd className="min-w-0 break-words text-zinc-200">
        {attendees.map((a) => (
          // Attendee objects don't carry a stable id; key by the
          // rendered string. Two rows with the exact same display
          // are deduped on render, which is what the user perceives
          // anyway.
          <span key={`${label}-${formatAttendee(a)}`} className="mr-2 inline-block">
            {formatAttendee(a)}
          </span>
        ))}
      </dd>
    </>
  );
}

function formatAttendee(a: EmailAttendee): string {
  if (a.display_name && a.email) return `${a.display_name} <${a.email}>`;
  return a.display_name ?? a.email ?? '(no address)';
}

function EmailBody({ message }: { message: MessageDto }) {
  const hasText = !!message.body_text && message.body_text.trim().length > 0;
  const hasHtml = message.has_body_html || !!message.body_html;
  const [mode, setMode] = useState<'text' | 'html'>('text');

  // Default to text. If text is absent and HTML exists, auto-flip to
  // formatted so the operator sees something useful by default.
  const effectiveMode = !hasText && hasHtml ? 'html' : mode;

  return (
    <div className="space-y-2">
      {hasText && hasHtml ? (
        <div className="flex items-center gap-1 text-[10px]">
          <ModeToggle
            label="Plain text"
            active={effectiveMode === 'text'}
            onClick={() => setMode('text')}
          />
          <ModeToggle
            label="View formatted email"
            active={effectiveMode === 'html'}
            onClick={() => setMode('html')}
          />
        </div>
      ) : null}

      {effectiveMode === 'html' && hasHtml ? (
        <EmailHtmlFrame html={message.body_html ?? ''} />
      ) : hasText ? (
        <div className="whitespace-pre-wrap break-words text-sm text-zinc-100">
          {message.body_text}
        </div>
      ) : (
        <div className="text-sm italic text-zinc-500">
          No body content (attachment-only or empty message).
        </div>
      )}
    </div>
  );
}

function ModeToggle({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`email-mode-${label.split(' ')[0]?.toLowerCase()}`}
      aria-pressed={active}
      className={`rounded px-1.5 py-0.5 ${
        active
          ? 'bg-zinc-800 text-zinc-100 ring-1 ring-inset ring-zinc-700'
          : 'border border-zinc-800 text-zinc-400 hover:bg-zinc-900'
      }`}
    >
      {label}
    </button>
  );
}

function EmailHtmlFrame({ html }: { html: string }) {
  // Sandbox notes:
  //   - `sandbox=""` blocks scripts, top-level navigation, popups,
  //     form submission, downloads, and same-origin access. The
  //     iframe runs as a unique origin, so cookies/localStorage etc.
  //     are isolated. We deliberately omit `allow-scripts` and any
  //     other allow-* flag.
  //   - we use `srcDoc` rather than `src` so the HTML is delivered
  //     inline; no extra HTTP request.
  //   - the frame is given a fixed minimum height; the operator can
  //     scroll within it. A `Math.min/max` resize loop is plausibly
  //     useful later but kept out of this batch.
  return (
    <iframe
      data-testid="email-html-frame"
      title="Email content (sandboxed)"
      sandbox=""
      referrerPolicy="no-referrer"
      srcDoc={hardenedEmailSrcDoc(html)}
      className="block h-80 w-full rounded border border-zinc-800 bg-white"
    />
  );
}

function PaperclipIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}
